import { Alert, Box, Divider, Snackbar } from "@mui/material";
import clsx from "clsx";
import i18n from "i18next";
import { observer } from "mobx-react-lite";
import { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import useDesignEditorUtils from "../../../DesignEditor/CustomUtils/UseDesignEditor.tsx";
import { DesignEditorContext } from "../../../DesignEditor/contexts/DesignEditor.tsx";
import { useEditor } from "../../../DesignEditor/hooks/useEditor.tsx";
import useWizardImagesHeader from "../../../DesignEditor/hooks/useWizardImagesHeader.tsx";
import { IScene } from "../../../DesignEditor/types";
import {
	ObjectsEnum,
	SecondaryTabTypeEnum,
} from "../../../DesignEditor/views/DesignEditor/components/Panels/panelItems";
import { useSmartImageUtils } from "../../../DesignEditor/views/DesignEditor/utils/smartImageUtils.ts";
import BulkModeIcon from "../../../assets/images/icons/BulkModeIcon.tsx";
import ContentModerationIcon from "../../../assets/images/icons/ContentModerationIcon.tsx";
import SingleModeIcon from "../../../assets/images/icons/SingleModeIcon.tsx";
import BackButton from "../../../components/common/BackButton/BackButton.tsx";
import BriaButton from "../../../components/common/BriaButton/BriaButton";
import BriaTooltip from "../../../components/common/BriaTooltip/BriaTooltip.tsx";
import ConfirmationPopup from "../../../components/common/ConfirmationPopup/ConfirmationPopup";
import ExportButton from "../../../components/common/ExportButton/ExportButton.tsx";
import FullScreenViewer from "../../../components/common/FullScreenViewer/FullScreenViewer";
import { FullScreenImageType } from "../../../components/common/FullScreenViewer/Image/FullScreenImage.tsx";
import { BriaImageProps } from "../../../components/common/Galleries/BriaImage.tsx";
import useSelectable from "../../../components/common/Selectable/useSelectable";
import { APPS, IMAGES_FORMATS } from "../../../constants/AppsConstants.ts";
import LinkConstants from "../../../constants/LinkConstants.ts";
import { useLogEvents } from "../../../hooks/useLogEvents.ts";
import { useAppStore } from "../../../hooks/useStores";
import {
	ImageEditingResetBehavior,
	ImageEditingViewMode,
	ImageToImageConfigType,
	PlaygroundImage,
} from "../../../models/image-to-image";
import { IframePostMessageActionTypes, IframePostMessageItemTypes } from "../../../models/new-iframe.ts";
import { TextToImageConfigType } from "../../../models/text-to-image.ts";
import { isAutomotiveApp, isCampaignEditor, isFoxApps, isTextToVector } from "../../../utils";
import { getFileExtensionFromUrl, getSafeFileName } from "../../../utils/index.ts";
import { supportedImageFileTypes } from "../../../utils/useImageUtils.tsx";
import iframeStore from "../../IframeNew/iframe-store.tsx";
import { SaveImagesLoadingState } from "../store/playground-store.tsx";
import styles from "./PlaygroundActionsHeader.module.scss";

const PlaygroundActionsHeader = () => {
	const { t } = useTranslation("translation", { keyPrefix: "playground.actionsHeader" });
	const { playgroundStore, imagesStore, imageToImageStore, designEditorStore, authStore, textToImageStore } =
		useAppStore();
	const { logImageDownloadEvents } = useLogEvents();
	const { selectAll } = useSelectable(playgroundStore.getSelecto());
	const selectedImages = playgroundStore.getSelectedImages();
	const availableImages = playgroundStore.getAvailableImages();
	const [clearWarningPopup, setClearWarningPopup] = useState(false);
	const [saveToMyCampaignLoading, setSaveToMyCampaignLoading] = useState(false);
	const [isViewerOpen, setIsViewerOpen] = useState(false);
	const { addImageObjectToCanvas } = useDesignEditorUtils();
	const { isPopupView, scenes, setCurrentScene, setScenes } = useContext(DesignEditorContext);
	const editor = useEditor();
	const { uploadAndRegisterBackgroundImages, resetRectangleInAGroup, findObjectFromScene } = useSmartImageUtils();
	const { uploadAndRegisterNewWizardImages } = useWizardImagesHeader();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const handleSelectAll = () => {
		selectAll();
		playgroundStore.handleSelectImagesElements(playgroundStore.getSelecto()?.getSelectedTargets());
	};
	const isSingleMode =
		iframeStore.isIframe() && playgroundStore.selectedConfig === APPS.AUTOMOTIVE
			? playgroundStore.automotiveViewMode === ImageEditingViewMode.SINGLE
			: playgroundStore.imageEditingViewMode === ImageEditingViewMode.SINGLE;
	const isRestInIframeImageToImage =
		iframeStore.isIframe() &&
		playgroundStore.selectedConfig === APPS.IMAGE_TO_IMAGE &&
		iframeStore.iframe.config.image_editing_config?.default_reset_behavior === ImageEditingResetBehavior.RESET;

	const defaultAcceptTypes = supportedImageFileTypes.map((type) => `image/${type}`).join(", ");
	const [isErrorExistInSelectedImages, setIsErrorExistInSelectedImages] = useState<boolean>(false);

	useEffect(() => {
		handleNumResultsChange();
	}, []);

	useEffect(() => {
		setIsErrorExistInSelectedImages(selectedImages.some((image) => image.error !== undefined));
	}, [selectedImages]);

	const SaveTheImageToMyAssetsAndAdditToCanvas = async () => {
		await playgroundStore.saveMyImages(SaveImagesLoadingState.SaveAndAddToCanvas);
		for (const imageUrl of playgroundStore.uploadedImagesURLs) {
			addImageObjectToCanvas({
				url: imageUrl,
				id: uuidv4(),
				visual_hash: uuidv4(),
			});
		}
		setTimeout(() => {
			playgroundStore.showSuccessToastSavedImage = false;
			playgroundStore.closeModal();
		}, 1000);
		playgroundStore.uploadedImagesURLs = [];
	};

	const saveTextToImageResultToCampaign = async () => {
		setSaveToMyCampaignLoading(true);
		const selectedImages = playgroundStore.getSelectedImages();
		const firstSelectedImage = selectedImages[0];
		const url = firstSelectedImage.url;
		if (editor) {
			const _currentScene = editor.scene.exportToJSON();
			if (!imagesStore.isLoadingWizardImages) {
				await uploadAndRegisterBackgroundImages({} as ChangeEvent<HTMLInputElement>, url, _currentScene);
				await resetRectangleInAGroup(_currentScene, ObjectsEnum.OuterRectangle);
				const updatedPreview = (await editor?.renderer.render(_currentScene)) as string;
				const updatedScene = { ..._currentScene, preview: updatedPreview, duration: 1000 };
				const updatedScenes = scenes.map((scene: IScene) => {
					if (scene.id === updatedScene.id) {
						return { ...updatedScene, preview: updatedPreview };
					}
					return editor.scene.formalizeSceneAttributes(scene);
				}) as IScene[];
				setScenes(updatedScenes);
				if (isPopupView) designEditorStore.setProperty("isSceneFullyLoaded", false);
				setCurrentScene(updatedScene);
			} else {
				const activeObject = editor?.canvas.canvas.getActiveObject();
				let currentActiveImage = activeObject && (await findObjectFromScene(_currentScene, activeObject.id));
				if (!currentActiveImage) {
					currentActiveImage = designEditorStore.originalActiveObject;
				}
				currentActiveImage &&
					(await uploadAndRegisterNewWizardImages(
						{} as ChangeEvent<HTMLInputElement>,
						url,
						designEditorStore.currentSceneInWizard,
						currentActiveImage,
					));
				imagesStore.setProperty("isLoadingWizardImages", false);
			}
		}

		playgroundStore.showSuccessToastSavedImage = false;
		playgroundStore.closeModal();

		setSaveToMyCampaignLoading(false);
	};

	const hideToastHandler = () => {
		playgroundStore.showSuccessToastSavedImage = false;
	};

	const handleUploadButtonClick = () => {
		fileInputRef.current?.click();
	};

	const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			imageToImageStore.handleUploadImages(e);
		}
	};

	const onDownload = async () => {
		const images = selectedImages.length ? selectedImages : availableImages;
		if (images.length) {
			playgroundStore.exportImages(images);
			const firstImage = images[0];
			const defaultFileType = isTextToVector() ? IMAGES_FORMATS.SVG : IMAGES_FORMATS.PNG;
			const fileType = (firstImage?.config as TextToImageConfigType)?.format?.toUpperCase() || defaultFileType;
			logImageDownloadEvents(images, fileType);
		}
	};

	const onIframeSave = async () => {
		const images = selectedImages.length ? selectedImages : availableImages;
		if (images.length) {
			iframeStore.sendExportPostMessage(
				IframePostMessageActionTypes.Save,
				IframePostMessageItemTypes.Image,
				images.map((image) => ({
					type: IframePostMessageItemTypes.Image,
					src: image.url,
					fileName: `${getSafeFileName(playgroundStore.getFileName(image))}.${getFileExtensionFromUrl(image.url)}`,
				})),
			);
		}
	};

	const handleContentModerationMode = () => {
		imageToImageStore.handleConfigChange(
			"content_moderation",
			!(imageToImageStore.config?.content_moderation ?? false),
		);
		textToImageStore.handleConfigChange(
			"content_moderation",
			!(textToImageStore.config?.content_moderation ?? false),
		);
	};

	const imageEditingEnableBulk = iframeStore.iframe?.config.image_editing_config?.enable_bulk ?? false;
	const automotiveEnableBulk = iframeStore.iframe?.config.automotive_config?.enable_bulk ?? false;
	const hasEnabledGeneralConfig =
		iframeStore.iframe?.config.general_config?.enable_upload ||
		iframeStore.iframe?.config.general_config?.enable_download ||
		iframeStore.iframe?.config.general_config?.enable_save;
	const shouldHideButtonInIframe =
		iframeStore.isIframe() &&
		((playgroundStore.selectedConfig !== APPS.AUTOMOTIVE && !imageEditingEnableBulk) ||
			(playgroundStore.selectedConfig === APPS.AUTOMOTIVE && !automotiveEnableBulk));

	const shouldShowButton =
		(playgroundStore.selectedConfig === APPS.IMAGE_TO_IMAGE ||
			playgroundStore.selectedConfig === APPS.AUTOMOTIVE) &&
		!isFoxApps() &&
		!playgroundStore?.isText2ImagePopupOpened &&
		!shouldHideButtonInIframe;
	if (
		iframeStore.isIframe() &&
		playgroundStore.selectedConfig === APPS.AUTOMOTIVE &&
		!automotiveEnableBulk &&
		!iframeStore.iframe.config.general_config?.enable_upload &&
		!iframeStore.iframe.config.general_config?.enable_download &&
		!iframeStore.iframe.config.general_config?.enable_save
	) {
		return null;
	}

	const handleNumResultsChange = () => {
		if (
			playgroundStore.selectedConfig !== APPS.AUTOMOTIVE &&
			playgroundStore.selectedConfig !== APPS.IMAGE_TO_IMAGE
		)
			return;

		if (playgroundStore.imageEditingViewMode === ImageEditingViewMode.SINGLE) {
			imageToImageStore.handleNumResultsChange(1);
		} else {
			imageToImageStore.handleNumResultsChange(4);
		}
	};

	const handleViewModeChange = () => {
		playgroundStore.togglePlaygroundViewMode();
		if (
			playgroundStore.selectedConfig === APPS.AUTOMOTIVE ||
			playgroundStore.selectedConfig === APPS.IMAGE_TO_IMAGE
		) {
			imageToImageStore.resetConfigChange();
		}
		handleNumResultsChange();
	};
	return (
		<Box className={clsx(styles.root, styles.centerActions)}>
			<Box className={clsx(styles.actionsContainer)}>
				{shouldShowButton && (
					<BriaButton
						className={styles.actionButton}
						buttonType="textSmall"
						startIcon={!isSingleMode ? <SingleModeIcon /> : <BulkModeIcon />}
						onClick={handleViewModeChange}
					>
						{!isSingleMode ? t("singleMode") : t("bulkMode")}
					</BriaButton>
				)}
				{shouldShowButton && !iframeStore.isIframe() && (
					<BriaTooltip
						title={`${t("contentModeration.tooltip.info")} <a href="${
							LinkConstants.CONTENT_MODERATION_BRIA_API_DOC
						}" target="_blank" rel="noopener noreferrer">Learn more</a>`}
						keepToolTipOnHover={true}
					>
						<BriaButton
							className={clsx(styles.actionButton, {
								[styles.actionButtonFill]:
									(imageToImageStore.config?.content_moderation ?? false) &&
									playgroundStore.selectedConfig == APPS.IMAGE_TO_IMAGE,
								[styles.actionButtonFillAutomotive]:
									(imageToImageStore.config?.content_moderation ?? false) &&
									playgroundStore.selectedConfig == APPS.AUTOMOTIVE,
							})}
							buttonType="textSmall"
							startIcon={<ContentModerationIcon />}
							onClick={handleContentModerationMode}
						>
							{t("contentModeration.label")}
						</BriaButton>
					</BriaTooltip>
				)}

				{!isSingleMode && playgroundStore.selectedConfig !== APPS.PRODUCT_PLACEMENT && !isPopupView && (
					<BriaButton className={clsx(styles.actionButton)} buttonType="textSmall" onClick={handleSelectAll}>
						{t("selectAll")}
					</BriaButton>
				)}
				{!isAutomotiveApp() && (
					<BriaButton
						className={clsx(styles.actionButton)}
						buttonType="textSmall"
						onClick={() =>
							(playgroundStore.playgroundResults.length ||
								playgroundStore.textResults.length ||
								playgroundStore.playgroundVideoResults.length) &&
							setClearWarningPopup(true)
						}
					>
						{isRestInIframeImageToImage ? t("reset") : t("clear")}
					</BriaButton>
				)}
				<ConfirmationPopup
					open={clearWarningPopup}
					title={isRestInIframeImageToImage ? t("resetClearWarning.title") : t("clearWarning.title")}
					description={
						isRestInIframeImageToImage ? t("resetClearWarning.description") : t("clearWarning.description")
					}
					confirmButtonText={t("clearWarning.confirmBtn")}
					firstButtonText={t("clearWarning.closeBtn")}
					onClick={() => {
						isRestInIframeImageToImage ? playgroundStore.ResetResults() : playgroundStore.clearResults();
						setClearWarningPopup(false);
						if (playgroundStore.selectedConfig === APPS.IMAGE_TO_IMAGE && !isFoxApps()) {
							imageToImageStore.abortImageGeneration();
						}
					}}
					onClose={() => setClearWarningPopup(false)}
				/>
				{!isSingleMode && (
					<>
						{selectedImages.length > 0 && (
							<BriaButton
								className={clsx(styles.actionButton)}
								buttonType="textSmall"
								onClick={() => setIsViewerOpen(true)}
								disabled={isErrorExistInSelectedImages}
							>
								{t("fullScreenView.button")}
							</BriaButton>
						)}
					</>
				)}
				{playgroundStore?.isText2ImagePopupOpened &&
					!isPopupView &&
					imagesStore.secondaryTabType !== SecondaryTabTypeEnum.SMART_IMAGE &&
					!imagesStore.isLoadingWizardImages && (
						<>
							<BriaButton
								buttonType="textSmall"
								onClick={async () => {
									await playgroundStore.saveMyImages(SaveImagesLoadingState.Save);
									playgroundStore.uploadedImagesURLs = [];
								}}
								loading={playgroundStore?.loadingSaveToMyImages}
								className={clsx(styles.actionButton)}
								disabled={selectedImages.length == 0}
							>
								{t("saveToMyAssets")}
							</BriaButton>
							<BriaButton
								buttonType="secondaryMedium"
								onClick={SaveTheImageToMyAssetsAndAdditToCanvas}
								loading={playgroundStore?.loadingSaveToMyImagesAndInsertImageToAd}
								className={clsx(styles.actionButton, styles.secondaryActionBtn)}
								disabled={selectedImages.length == 0}
							>
								{t("insertAndClose")}
							</BriaButton>
						</>
					)}
				{((playgroundStore?.isText2ImagePopupOpened && isPopupView) ||
					imagesStore.isLoadingWizardImages ||
					(playgroundStore?.isText2ImagePopupOpened &&
						imagesStore.secondaryTabType === SecondaryTabTypeEnum.SMART_IMAGE)) && (
					<BriaButton
						buttonType="secondaryMedium"
						onClick={saveTextToImageResultToCampaign}
						loading={saveToMyCampaignLoading}
						className={clsx(styles.actionButton, styles.secondaryActionBtn)}
						disabled={selectedImages.length == 0 || saveToMyCampaignLoading}
					>
						{t("saveToMyCampaign")}
					</BriaButton>
				)}
				{!isCampaignEditor &&
					playgroundStore.selectedConfig !== APPS.PRODUCT_PLACEMENT &&
					(!iframeStore.isIframe() ||
						(playgroundStore.selectedConfig === APPS.AUTOMOTIVE
							? iframeStore.iframe?.config?.automotive_config?.enable_bulk && hasEnabledGeneralConfig
							: hasEnabledGeneralConfig)) && (
						<Divider orientation="vertical" variant="middle" flexItem className={styles.divider} />
					)}
				{(playgroundStore.selectedConfig === APPS.IMAGE_TO_IMAGE ||
					playgroundStore.selectedConfig === APPS.AUTOMOTIVE) &&
					(!iframeStore.isIframe() || iframeStore.iframe.config.general_config?.enable_upload) && (
						<>
							<BriaButton
								className={styles.actionButton}
								buttonType="textSmall"
								onClick={handleUploadButtonClick}
								loading={authStore.isLoadingOrgSubscriptions}
								disabled={
									authStore.isLoadingOrgSubscriptions ||
									authStore.orgPassedFreeLimit ||
									authStore.isSubscriptionInactive
								}
							>
								{t("upload")}
							</BriaButton>
							<input
								ref={fileInputRef}
								type="file"
								accept={defaultAcceptTypes}
								style={{ display: "none" }}
								onChange={handleFileUpload}
								multiple
							/>
						</>
					)}
				{!isCampaignEditor &&
					(playgroundStore.selectedConfig === APPS.IMAGE_TO_IMAGE ||
						playgroundStore.selectedConfig === APPS.AUTOMOTIVE ||
						playgroundStore.selectedConfig === APPS.TEXT_TO_IMAGE) && (
						<ExportButton
							onDownload={onDownload}
							onIframeSave={onIframeSave}
							buttonType="secondaryMedium"
							disabled={isErrorExistInSelectedImages}
							className={clsx(styles.actionButton, styles.secondaryActionBtn)}
						/>
					)}
				<BackButton className={clsx(styles.actionButton, styles.backButton)} />
			</Box>
			<Snackbar
				autoHideDuration={2000}
				onClose={hideToastHandler}
				open={playgroundStore.showSuccessToastSavedImage}
				className={styles.saveSuccessToast}
			>
				<Alert onClose={hideToastHandler} severity="success">
					{t("saveImageSuccess")}
				</Alert>
			</Snackbar>
			{isViewerOpen && (
				<FullScreenViewer
					open={isViewerOpen}
					images={playgroundStore.getSelectedImages().map((image) => ({
						src: image.url,
						fileName: playgroundStore.getFileName(image),
						compareTo: (image.config as ImageToImageConfigType)?.original_image?.url,
						compareLabel:
							isFoxApps() && image.type === "imageToImage"
								? `${i18n.t("playground.imageToImage.result.gallery.variationLabel", {
										variationNum: image.variationNum?.toString().padStart(2, "0"),
									})}`
								: undefined,
					}))}
					onClose={() => setIsViewerOpen(false)}
					displayMode="scroll"
					onDownload={(fullScreenImages: FullScreenImageType[]) =>
						playgroundStore.onFullScreenViewerDownload(
							playgroundStore.getSelectedImages() as (BriaImageProps & PlaygroundImage)[],
							fullScreenImages,
						)
					}
				/>
			)}
		</Box>
	);
};

export default observer(PlaygroundActionsHeader);
